import dayjs from 'dayjs'
import { kebabCase } from 'es-toolkit'
import type { Router } from 'vue-router'
import type { FallbackCategory, Game } from '#imports'
import type { ExperimentValueUnion } from '@core/types'
import { assetAbTestsFor } from './assetAbVariant'

import type { GameTrackingOrigin, GameTrackingMeta } from '../types/GameTracking'

export function fallbackListSlug(category: FallbackCategory): string {
    return `fallback:${kebabCase(category)}`
}

export enum GameListSlug {
    BINGO_LOBBY = 'bingo:lobby',
    BINGO_FAVOURITES = 'bingo:favourites',
    BINGO_FEATURED = 'bingo:featured',
    SAVED_GAMES = 'saved:games',
    SAVED_GAMES_ACCOUNT = 'saved:account',
    SAVED_GAMES_PLAYGAME = 'playgame:saved-games',
    VENDOR_GAMES = 'vendor:games',
}

function originUrl(): string {
    // tryUseNuxtApp()?.$router is `unknown` here (the harness doesn't augment NuxtApp's
    // router injection); cast to vue-router's Router to read currentRoute type-safely.
    const route = import.meta.client ? (tryUseNuxtApp()?.$router as Router | undefined)?.currentRoute.value : undefined

    return route?.path ?? useRequestURL().pathname
}

// Url-less callers are persistent components (heroes, winner tiles) that never
// render on /playgame — a url-less call there raced a pending navigation.
// Tracking on /playgame must pass meta.url (useGameOpenTracking) or is dropped.
function isPendingPlaygameArtifact(origin: GameTrackingOrigin, meta?: GameTrackingMeta): boolean {
    if (!import.meta.client || meta?.url !== undefined) return false

    const route = (tryUseNuxtApp()?.$router as Router | undefined)?.currentRoute.value
    if (!route?.meta?.isPlaygame) return false

    if (import.meta.dev) console.warn(`[tracking] dropped url-less ${origin} event on a playgame route — pass meta.url via useGameOpenTracking`)

    return true
}

/**
 * A tracking row with no url cannot be attributed to a page, so it is dropped
 * rather than sent with a NULL. Shared with `useGameImpressions` so both streams
 * drop for the same reason and say so in dev.
 */
export function hasTrackableUrl(url: unknown, origin: GameTrackingOrigin, event: string): url is string {
    if (typeof url === 'string' && url.length > 0) return true

    if (import.meta.dev) console.warn(`[tracking] dropped ${origin} ${event} without a url`)

    return false
}

// Bucket stamp for a game under a TileGen asset test, read from the instance
// the `game:asset-ab` plugin warms: this runs from click handlers, outside setup.
function assetVariantMeta(slug: string): { variant?: ExperimentValueUnion } {
    return assetAbTestsFor(tryUseNuxtApp())?.trackingMeta(slug) ?? {}
}

// A same-game re-click within the window is a load-retry, not new intent —
// drop the tracking event only; the game start is untouched.
const DUPLICATE_CLICK_WINDOW_MS = 30_000
const lastClickAt = new Map<string, number>()

export function trackGameOpen(origin: GameTrackingOrigin, game: Pick<Game, 'slug'>, meta?: GameTrackingMeta) {
    if (isPendingPlaygameArtifact(origin, meta)) return

    const entry = { origin, gameSlug: game.slug, url: originUrl(), ...assetVariantMeta(game.slug), ...meta }
    if (!hasTrackableUrl(entry.url, origin, 'click')) return

    if (import.meta.client) {
        // The shadow is always the same tile in the same list on the same
        // page — keep cross-list re-clicks of a game countable.
        const key = [origin, meta?.listSlug, entry.url, game.slug].join(':')
        const last = lastClickAt.get(key)
        const now = dayjs().valueOf()
        if (last !== undefined && now - last < DUPLICATE_CLICK_WINDOW_MS) return
        lastClickAt.set(key, now)
    }

    const { trackEvent } = useTracking()

    trackEvent({
        event_name: 'game-click',
        event_type: 'Click',
        meta_data: [entry],
    })
}

export function trackGameImpression(origin: GameTrackingOrigin, game: Pick<Game, 'slug'>, meta?: GameTrackingMeta) {
    if (isPendingPlaygameArtifact(origin, meta)) return

    const entry = { origin, gameSlug: game.slug, url: originUrl(), ...assetVariantMeta(game.slug), ...meta }
    if (!hasTrackableUrl(entry.url, origin, 'impression')) return

    const { trackEvent } = useTracking()

    trackEvent({
        event_name: 'game-impression',
        event_type: 'Load',
        meta_data: [entry],
    })
}

/**
 * Search funnel events: `search-open` when the search opens, `search-query` once a
 * query's results are on screen. Together with the game clicks from the overlay,
 * which carry the same `searchId`, they give bounce rate and click-through rate.
 */
export function trackSearchEvent(event: 'search-open' | 'search-query', meta?: GameTrackingMeta) {
    const entry = { origin: 'search', url: originUrl(), ...meta }
    if (!hasTrackableUrl(entry.url, 'search', event)) return

    const { trackEvent } = useTracking()

    trackEvent({
        event_name: event,
        event_type: event === 'search-open' ? 'Click' : 'Load',
        meta_data: [entry],
    })
}
